import {
  TextField as MaterialUiTextField,
  TextFieldProps as MaterialUiTextFieldProps
} from '@mui/material'
import {
  CommonFieldProps,
  CommonInputProps,
  ContentWrapper,
  GridWrapper,
  GridWrapperProps,
  HideableFieldProps
} from '.'
import {
  useFieldLabelAndPlaceholder,
  useFieldProps,
  useHelperText
} from '../hooks'
import { replaceUnnecessarySpaces } from '../utils'

type CustomizedTextFieldProps = Pick<
  MaterialUiTextFieldProps,
  'disabled' | 'id' | 'onBlur' | 'onChange' | 'type' | 'value'
> &
  CommonInputProps &
  CommonFieldProps &
  Partial<GridWrapperProps>

const CustomizedTextField = ({
  name,
  executeOnChangeFn,
  variant,
  ...rest
}: CustomizedTextFieldProps) => {
  const { error, onBlur, onChange, setValue, value } =
    useFieldProps<string>(name)

  const updateValue = (value: string, replaceSpaces: boolean) => {
    const newValue =
      replaceSpaces && typeof value === 'string'
        ? replaceUnnecessarySpaces(value)
        : value
    setValue(newValue, true)
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { target } = event

    updateValue(target.value, false)

    onChange(event)

    if (executeOnChangeFn) {
      executeOnChangeFn()
    }
  }

  const handleBlur = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateValue(value, true)

    onBlur(event)
  }

  return (
    <TextField
      {...rest}
      error={error}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      type="text"
      value={value}
      variant={variant || 'standard'}
    />
  )
}

export type FormikTextFieldProps = HideableFieldProps & CustomizedTextFieldProps

export const FormikTextField = ({
  hidefield,
  ...rest
}: FormikTextFieldProps) => {
  return (
    <ContentWrapper showContent={!hidefield}>
      <CustomizedTextField {...rest} />
    </ContentWrapper>
  )
}

interface TextFieldProps
  extends Omit<CustomizedTextFieldProps, 'executeOnChange' | 'helperText'> {
  error?: string
}

export const TextField = ({
  error,
  fontSize,
  gridProps,
  label,
  labelTranslationId,
  name,
  placeholder,
  placeholderTranslationId,
  ...rest
}: TextFieldProps) => {
  const helperText = useHelperText(error)

  const { fieldLabel, fieldPlaceholder } = useFieldLabelAndPlaceholder(
    labelTranslationId,
    label,
    placeholderTranslationId,
    placeholder
  )

  return (
    <GridWrapper gridProps={gridProps || { md: 4 }}>
      <MaterialUiTextField
        {...rest}
        id={name}
        InputProps={{
          sx: { fontSize }
        }}
        name={name}
        error={!!error}
        helperText={helperText}
        label={fieldLabel}
        placeholder={fieldPlaceholder}
      />
    </GridWrapper>
  )
}
